import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {ArrowUpRight, BadgeCheck, Facebook} from 'lucide-react';
import {ProfessionalsQuery} from './__generated__/ProfessionalsQuery.graphql';

const QUERY = graphql`
    query ProfessionalsQuery {
        talent {
            id
            name
            slug
            roles
            headline
            bio
            skills
            dayRate
            hourRate
            portraitUrl
            availability
            portfolioLinks { label url }
            facebookProfileUrl
            facebookPageUrl
            facebookPageName
            facebookPageVerified
        }
    }`;

type Talent = ProfessionalsQuery['response']['talent'][number];

const roleLabel = (role: string) => role.replace(/_/g, ' ').toLowerCase();

const rateLabel = (t: Talent): string | null => {
    if (t.dayRate) return `$${Number(t.dayRate).toLocaleString()}/day`;
    if (t.hourRate) return `$${Number(t.hourRate).toLocaleString()}/hr`;
    return null;
};

const StatusIndicator: React.FC<{ status: string }> = ({status}) => {
    const colors: Record<string, string> = {
        AVAILABLE: 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.6)]',
        BOOKED: 'bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.6)]',
        UNAVAILABLE: 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.6)]',
    };

    return (
        <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${colors[status] || 'bg-gray-500'} animate-pulse`}></div>
            <span className="text-[10px] uppercase tracking-widest text-gray-400">{status.toLowerCase()}</span>
        </div>
    );
};

const Portrait: React.FC<{ talent: Talent }> = ({talent}) => {
    if (talent.portraitUrl) {
        return (
            <img
                src={talent.portraitUrl}
                alt={talent.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 grayscale group-hover:grayscale-0"
            />
        );
    }
    const initials = talent.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
    return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-lumina-purple/30 to-lumina-accent/20">
            <span className="text-5xl font-display font-bold text-white/70">{initials}</span>
        </div>
    );
};

// Their professional Page first (with a check when they proved they manage it), then
// their personal profile, as compact icon links.
const FacebookLinks: React.FC<{ talent: Talent }> = ({talent}) => {
    const linkClass = 'inline-flex items-center gap-0.5 p-1 text-gray-500 hover:text-white transition-colors';
    return (
        <>
            {talent.facebookPageUrl && (
                <a
                    href={talent.facebookPageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClass}
                    title={talent.facebookPageName ?? 'Facebook Page'}
                    aria-label={`${talent.facebookPageName ?? talent.name} on Facebook`}
                >
                    <Facebook className="w-4 h-4"/>
                    {talent.facebookPageVerified && <BadgeCheck className="w-3 h-3 text-lumina-accent" aria-label="Verified Page"/>}
                </a>
            )}
            {talent.facebookProfileUrl && !talent.facebookPageUrl && (
                <a
                    href={talent.facebookProfileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClass}
                    title="Facebook profile"
                    aria-label={`${talent.name} on Facebook`}
                >
                    <Facebook className="w-4 h-4"/>
                </a>
            )}
        </>
    );
};

const TalentCard: React.FC<{ talent: Talent }> = ({talent}) => {
    const rate = rateLabel(talent);
    const primaryLink = talent.portfolioLinks[0];
    const ctaClass = 'flex items-center gap-1 text-xs font-bold text-lumina-accent hover:text-white transition-colors';

    return (
        <div className="group relative flex flex-col bg-[#0a0a12] border border-gray-800 rounded-xl overflow-hidden hover:border-lumina-accent/50 transition-all duration-300">
            <div className="h-64 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-transparent to-transparent z-10"></div>
                <Portrait talent={talent}/>
                <div className="absolute top-3 right-3 z-20 bg-black/60 backdrop-blur-md border border-gray-700/60 px-2.5 py-1 rounded-full">
                    <StatusIndicator status={talent.availability}/>
                </div>
            </div>

            <div className="flex flex-col flex-1 p-6 pt-0 relative z-20 -mt-10">
                <div className="mb-4">
                    <h3 className="text-xl font-display font-bold text-white leading-tight">{talent.name}</h3>
                    {talent.headline && <p className="text-xs text-lumina-accent mt-1">{talent.headline}</p>}
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2 leading-relaxed">
                        {talent.roles.map(roleLabel).join(' · ')}
                    </p>
                </div>

                {talent.bio && <p className="text-gray-400 text-sm mb-5 line-clamp-2">{talent.bio}</p>}

                {talent.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-5">
                        {talent.skills.slice(0, 4).map((skill) => (
                            <span key={skill} className="text-[10px] bg-gray-900 text-gray-300 border border-gray-800 px-2 py-0.5 rounded">
                                {skill}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-auto flex items-center gap-3 pt-4 border-t border-gray-800">
                    <span className="text-sm font-mono text-gray-300 flex-1">{rate ?? 'Rate on request'}</span>
                    <FacebookLinks talent={talent}/>
                    {primaryLink ? (
                        <a href={primaryLink.url} target="_blank" rel="noreferrer" className={ctaClass}>
                            {primaryLink.label.toUpperCase()} <ArrowUpRight className="w-3 h-3"/>
                        </a>
                    ) : (
                        <a href="/contact" className={ctaClass}>
                            BOOK <ArrowUpRight className="w-3 h-3"/>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

const Roster: React.FC = () => {
    const data = useLazyLoadQuery<ProfessionalsQuery>(QUERY, {});
    if (data.talent.length === 0) {
        return <p className="text-gray-500 text-sm">Our roster is being updated. Contact us for crew availability.</p>;
    }
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.talent.map((t) => <TalentCard key={t.id} talent={t}/>)}
        </div>
    );
};

const Professionals: React.FC = () => {
    return (
        <section id="talent" className="py-24 bg-[#050508] relative border-t border-gray-900">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-lumina-accent/10 to-transparent"></div>
                <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-lumina-purple/10 to-transparent"></div>
            </div>

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                            OUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-lumina-accent to-lumina-purple">CREW</span>
                        </h2>
                        <p className="text-gray-400 max-w-2xl text-lg">
                            Photographers, videographers and editors available to augment your production,
                            on their own or alongside our gear.
                        </p>
                    </div>
                    <a href="/contact" className="flex items-center gap-2 text-sm font-bold text-lumina-accent border-b border-lumina-accent pb-1 hover:text-white hover:border-white transition-all">
                        BOOK CREW <ArrowUpRight className="w-4 h-4"/>
                    </a>
                </div>

                <Suspense fallback={<p className="text-gray-500 text-sm">Loading roster…</p>}>
                    <Roster/>
                </Suspense>
            </div>
        </section>
    );
};

export default Professionals;
